import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { FormDatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { JOVEO } from '@/types/channels';
import { durationInDays, formatShortDate } from '@/utils/date-util';

interface DatesProps {
  campaignStartsAt?: string;
}

export function Dates({ campaignStartsAt }: DatesProps) {
  const intl = useIntl();
  const { channelSlug } = useParams();
  const { campaign, hookForm, isFetching, isReach, isCompleted } =
    useEditCampaignContext();
  const { control, watch } = hookForm;

  const hiringGoal = campaign?.hiring_goal;
  const hiringGoalStartAt = hiringGoal?.start_date;
  const hiringGoalEndAt = hiringGoal?.hire_by;
  const hiringGoalDuration = hiringGoal?.duration;
  const isReadonly = campaign?.is_readonly;

  const startsAt = watch('starts_at');
  const endAt = watch('end_at');
  const duration = endAt && startsAt ? durationInDays(startsAt, endAt) : null;

  return (
    <div className="flex w-full gap-4">
      <div className="flex w-full flex-col gap-1">
        <FormDatePicker
          control={control}
          name="starts_at"
          label={intl.formatMessage({ defaultMessage: 'Start date' })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter start date',
          })}
          isDisabled={!!campaignStartsAt || isCompleted || isReadonly}
          isStartOfDay
          isFetching={isFetching}
        />
        {!hiringGoalStartAt && isFetching && (
          <Skeleton className="w-50 my-0.5 h-3" />
        )}
        {hiringGoalStartAt && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Active hiring goal started {startsAt}"
              values={{ startsAt: formatShortDate(hiringGoalStartAt) }}
            />
          </Typography>
        )}
      </div>

      <div className="flex w-full flex-col gap-1">
        <FormDatePicker
          control={control}
          name="end_at"
          label={intl.formatMessage({ defaultMessage: 'End date' })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter end date',
          })}
          isEndOfDay
          isFetching={isFetching}
          isDisabled={
            isReach || channelSlug === JOVEO || isCompleted || isReadonly
          }
        />
        {!hiringGoalEndAt && isFetching && (
          <Skeleton className="w-50 my-0.5 h-3" />
        )}
        {hiringGoalEndAt && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Active hiring goal ends {endAt}"
              values={{ endAt: formatShortDate(hiringGoalEndAt) }}
            />
          </Typography>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Typography variant="subtitle2">
          <FormattedMessage defaultMessage="Duration" />
        </Typography>

        <Typography
          variant="body1"
          className="flex h-8 items-center justify-start"
        >
          {!duration && isFetching && <Skeleton className="h-4 w-12" />}
          {duration && (
            <FormattedMessage
              defaultMessage="{duration} days"
              values={{ duration }}
            />
          )}
        </Typography>

        <Typography
          variant="small1"
          className="!text-fx-neutral-600 flex flex-1 items-end"
        >
          {!hiringGoalDuration && isFetching && (
            <Skeleton className="h-3 w-10" />
          )}
          {hiringGoalDuration && (
            <FormattedMessage
              defaultMessage="{duration} days"
              values={{ duration: hiringGoalDuration }}
            />
          )}
        </Typography>
      </div>
    </div>
  );
}
